import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, Tag } from "lucide-react";
import { CONTACT, POPULAR_HERO_SERVICES } from "@/data/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1731168273756-e02cae42265b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxoYW5keW1hbiUyMHByb2Zlc3Npb25hbCUyMHdvcmtpbmclMjBob21lJTIwcmVub3ZhdGlvbnxlbnwwfHx8fDE3ODI5NjE3NTR8MA&ixlib=rb-4.1.0&q=85";

const EASE = [0.22, 1, 0.36, 1];

function RevealLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: EASE, delay }}
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-[100vh] flex items-center overflow-hidden isolate"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Professional handyman working on home renovation"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F]/95 via-[#0A192F]/85 to-[#0A192F]/70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(249,115,22,0.18),transparent_55%)]" />
      </motion.div>

      <div className="rs-container pt-36 pb-20 md:pt-44 md:pb-24 w-full">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="rs-overline mb-5"
          >
            RS Handyman Services • Salisbury, MD
          </motion.p>

          <h1 className="font-display font-semibold text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.98] tracking-tight uppercase">
            <RevealLine delay={0.25}>Your Home.</RevealLine>
            <RevealLine delay={0.37}>
              <span className="text-[#F97316]">Our Expertise.</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 text-lg md:text-xl text-slate-200/90 max-w-2xl leading-relaxed"
          >
            Professional Handyman Services &amp; Mobile Car Detailing across{" "}
            <span className="text-white font-medium">Maryland and Delaware</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4"
          >
            <a href="#contact" data-testid="hero-estimate-cta" className="rs-btn-primary">
              Get a Free Estimate
              <ArrowRight className="h-4 w-4" />
            </a>
            <a href="#pricing" data-testid="hero-prices-cta" className="rs-btn-ghost">
              <Tag className="h-4 w-4" />
              View Our Prices
            </a>
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="hero-call-cta" className="rs-btn-ghost">
              <Phone className="h-4 w-4" />
              Call Now
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-12"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/50 mb-3">
              Popular services • Starting prices
            </div>
            <div className="flex flex-wrap gap-2.5" data-testid="hero-popular-services">
              {POPULAR_HERO_SERVICES.map((s) => (
                <a
                  key={s.title}
                  href="#pricing"
                  className="group inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/15 pl-3 pr-1.5 py-1.5 text-sm text-white hover:bg-white/[0.1] hover:border-[#F97316]/60 transition-all"
                >
                  <span className="text-white/85">{s.title}</span>
                  <span className="rounded-full bg-[#F97316] text-white text-xs font-bold px-2.5 py-0.5">
                    {s.from}
                  </span>
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-[0.3em] uppercase hidden md:flex flex-col items-center gap-2"
      >
        Scroll
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-[#F97316] to-transparent"
        />
      </motion.div>
    </section>
  );
}
